import { FC, useMemo } from 'react';
import { BanIcon } from '@heroicons/react/outline';

import {
  MailSequenceClientPreview,
  MailSequenceReferenceDateType,
  MailSequenceScheduledSendStatus,
} from '../../../generated/graphql';
import ModalDialog from '../../components/ModalDialog';
import ProgramTagChip from '../../components/ProgramTag/ProgramTagChip';
import {
  computeOffsetDaysFromDates,
  getStepResourceMode,
  mergeStepActivitiesWithComposerOrder,
  StepActivitiesState,
  StepResourceModeState,
  TemplateStepDraft,
} from '../../lib/mailSequence';
import { SavedActivityInputPreview } from '../../lib/followUp';
import { DEFAULT_TAG_COLOR } from '../../lib/colors';
import Spinner from '../../svgs/Spinner';
import ScheduledSendCard from '../PatientProfile/Sequences/ScheduledSendCard';
import MailStepBadge from '../../components/MailStepBadge';
import { buildScheduledSendCaption, formatProgramDate } from './anchorDates';

interface PreviewRecipient {
  programInstanceId: string;
  title: string;
}

export type PreviewSelectedTag = {
  id: string;
  name: string;
  color: string;
  clientCount: number;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  loading: boolean;
  recipients: PreviewRecipient[];
  selectedTags: PreviewSelectedTag[];
  /** Clients enrolled manually without any recipient tag. */
  clientsWithoutTagsCount: number;
  previewByProgramId: Record<string, MailSequenceClientPreview>;
  hasPreviewData: boolean;
  steps: TemplateStepDraft[];
  defaultActivities: SavedActivityInputPreview[];
  stepActivitiesState: StepActivitiesState;
  stepResourceModes: StepResourceModeState;
  referenceDateType: MailSequenceReferenceDateType;
  defaultSendTime: string;
  anchorLabel: string;
}

const MAX_NAMED_RECIPIENTS = 3;

/** "Alex", "Alex and Jordan", "Alex, Jordan, and Sam", "Alex, Jordan, Sam, and 2 others" */
export const formatRecipientNamesList = (names: string[]): string => {
  const cleaned = names.map((n) => n.trim()).filter(Boolean);
  if (cleaned.length === 0) return 'Selected clients';
  if (cleaned.length === 1) return cleaned[0];
  if (cleaned.length === 2) return `${cleaned[0]} and ${cleaned[1]}`;
  if (cleaned.length === 3) {
    return `${cleaned[0]}, ${cleaned[1]}, and ${cleaned[2]}`;
  }
  const shown = cleaned.slice(0, MAX_NAMED_RECIPIENTS);
  const others = cleaned.length - MAX_NAMED_RECIPIENTS;
  return `${shown.join(', ')}, and ${others} ${
    others === 1 ? 'other' : 'others'
  }`;
};

const getStepActivityCount = (
  stepIndex: number,
  defaultActivities: SavedActivityInputPreview[],
  stepActivitiesState: StepActivitiesState,
  stepResourceModes: StepResourceModeState,
): number => {
  const mode = getStepResourceMode(stepResourceModes, stepIndex);
  if (mode === 'custom') {
    return (stepActivitiesState[stepIndex] ?? []).length;
  }
  const extras = stepActivitiesState[stepIndex] ?? [];
  if (extras.length === 0) {
    return defaultActivities.length;
  }
  return mergeStepActivitiesWithComposerOrder(
    [...defaultActivities, ...extras],
    defaultActivities,
  ).length;
};

const buildCaption = (
  scheduledDate: string,
  referenceDate: string | null | undefined,
  anchorLabel: string,
  willBeSkipped: boolean,
): { suffix: string; secondary?: string } => {
  const scheduledDateLabel =
    formatProgramDate(scheduledDate) ?? scheduledDate.slice(0, 10);
  if (!referenceDate) {
    return {
      suffix: willBeSkipped
        ? `sending on ${scheduledDateLabel}`
        : `for ${scheduledDateLabel}`,
    };
  }

  return buildScheduledSendCaption({
    status: willBeSkipped
      ? MailSequenceScheduledSendStatus.Skipped
      : MailSequenceScheduledSendStatus.Pending,
    scheduledDateLabel,
    offsetDays: computeOffsetDaysFromDates(referenceDate, scheduledDate),
    anchorLabel,
    anchorDateIso: referenceDate,
  });
};

const stepCircleClass =
  'inline-flex h-4 w-4 shrink-0 items-center justify-center self-center rounded-full bg-secondary-100 text-[10px] font-bold leading-none text-white';

/** Matches Patient Management "No tag" filter chip. */
const NoTagPill: FC = () => (
  <span className="inline-flex max-w-full items-center gap-2 rounded-full bg-neutral-50 px-3 py-0.5 text-caption ring-1 ring-neutral-100">
    <BanIcon className="h-3.5 w-3.5 min-w-[0.875rem] text-neutral-150" />
    <span className="max-w-[200px] truncate text-neutral-150">No tag</span>
  </span>
);

const TagCountRow: FC<{
  tag: PreviewSelectedTag;
}> = ({ tag }) => (
  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 pl-8">
    <ProgramTagChip
      tag={{
        name: tag.name,
        color: tag.color || DEFAULT_TAG_COLOR,
      }}
      className="py-0.5 text-caption"
      iconClassName="h-3.5 w-3.5 min-w-[0.875rem]"
    />
    <span>
      : <span className="font-medium text-neutral-150">{tag.clientCount}</span>
    </span>
  </div>
);

const ClientCountBreakdown: FC<{
  totalClients: number;
  selectedTags: PreviewSelectedTag[];
  clientsWithoutTagsCount: number;
}> = ({ totalClients, selectedTags, clientsWithoutTagsCount }) => {
  const showBreakdown =
    selectedTags.length > 1 ||
    (selectedTags.length >= 1 && clientsWithoutTagsCount > 0);

  if (!showBreakdown) {
    if (selectedTags.length === 1) {
      const tag = selectedTags[0];
      return (
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 pl-4 text-caption text-neutral-125">
          <span className="font-bold">Total clients with</span>
          <ProgramTagChip
            tag={{
              name: tag.name,
              color: tag.color || DEFAULT_TAG_COLOR,
            }}
            className="py-0.5 text-caption"
            iconClassName="h-3.5 w-3.5 min-w-[0.875rem]"
          />
          <span>
            :{' '}
            <span className="font-medium text-neutral-150">{totalClients}</span>
          </span>
        </div>
      );
    }

    return (
      <div className="pl-4 text-caption text-neutral-125">
        <span className="font-bold">Total clients:</span>{' '}
        <span className="font-medium text-neutral-150">{totalClients}</span>
      </div>
    );
  }

  return (
    <div className="space-y-1.5 text-caption text-neutral-125">
      <div className="pl-4">
        <span className="font-bold">Total clients:</span>{' '}
        <span className="font-medium text-neutral-150">{totalClients}</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 pl-8">
        <NoTagPill />
        <span>
          :{' '}
          <span className="font-medium text-neutral-150">
            {clientsWithoutTagsCount}
          </span>
        </span>
      </div>
      {selectedTags.map((tag) => (
        <TagCountRow key={tag.id} tag={tag} />
      ))}
    </div>
  );
};

const SchedulePreviewModal: FC<Props> = ({
  isOpen,
  onClose,
  loading,
  recipients,
  selectedTags,
  clientsWithoutTagsCount,
  previewByProgramId,
  hasPreviewData,
  steps,
  defaultActivities,
  stepActivitiesState,
  stepResourceModes,
  referenceDateType,
  defaultSendTime,
  anchorLabel,
}) => {
  const recipientsWithPreview = useMemo(
    () => recipients.filter((r) => previewByProgramId[r.programInstanceId]),
    [previewByProgramId, recipients],
  );

  const namesLine = useMemo(
    () => formatRecipientNamesList(recipientsWithPreview.map((r) => r.title)),
    [recipientsWithPreview],
  );

  const totalClients = recipientsWithPreview.length;

  const firstStep = 0;
  const lastStep = Math.max(steps.length - 1, 0);
  const isSingleStep = firstStep === lastStep || steps.length <= 1;

  const mailRange = isSingleStep ? (
    <MailStepBadge stepIndex={firstStep} />
  ) : (
    <span className="inline-flex items-baseline gap-1 font-medium">
      <span>Mail</span>
      <span className={stepCircleClass} aria-label={`Mail ${firstStep + 1}`}>
        {firstStep + 1}
      </span>
      <span className="font-normal">to</span>
      <span className={stepCircleClass} aria-label={`Mail ${lastStep + 1}`}>
        {lastStep + 1}
      </span>
    </span>
  );

  const sharedReferenceDates = useMemo(() => {
    const dates = recipientsWithPreview
      .map((r) => previewByProgramId[r.programInstanceId]?.referenceDate)
      .filter(Boolean)
      .map(String);
    return Array.from(new Set(dates));
  }, [previewByProgramId, recipientsWithPreview]);

  const isProgramDate =
    referenceDateType === MailSequenceReferenceDateType.ProgramStartDate ||
    referenceDateType === MailSequenceReferenceDateType.ProgramEndDate;
  const programDateNoun =
    referenceDateType === MailSequenceReferenceDateType.ProgramEndDate
      ? 'program end date'
      : 'program start date';

  const sendTime = defaultSendTime?.slice(0, 5) ?? '';

  const timingClause = useMemo(() => {
    const singleFormatted =
      sharedReferenceDates.length === 1
        ? formatProgramDate(sharedReferenceDates[0]) ??
          sharedReferenceDates[0].slice(0, 10)
        : null;

    if (isProgramDate) {
      const possessive =
        recipientsWithPreview.length === 1
          ? `${
              recipientsWithPreview[0].title.trim().split(/\s+/)[0] ||
              'the client'
            }'s`
          : "each client's";
      if (singleFormatted) {
        return `is scheduled from ${possessive} ${programDateNoun} (${singleFormatted})`;
      }
      return `is scheduled from ${possessive} ${programDateNoun}`;
    }

    if (singleFormatted) {
      return `is scheduled from ${singleFormatted}`;
    }
    if (sharedReferenceDates.length > 1) {
      return `is scheduled from each client's ${anchorLabel}`;
    }
    return 'is scheduled';
  }, [
    anchorLabel,
    isProgramDate,
    programDateNoun,
    recipientsWithPreview,
    sharedReferenceDates,
  ]);

  const sendClause = sendTime
    ? ` and will be sent at ${sendTime} on each date in the steps below`
    : '';

  const allShareSameSchedule = useMemo(() => {
    if (recipientsWithPreview.length <= 1) return true;
    const signatures = recipientsWithPreview.map((r) => {
      const preview = previewByProgramId[r.programInstanceId];
      if (!preview || preview.error) return `error:${r.programInstanceId}`;
      return JSON.stringify(
        preview.steps.map((s) => [
          s.stepIndex,
          s.scheduledDate,
          s.willBeSkippedAtEnroll,
          s.skipReason,
        ]),
      );
    });
    return signatures.every((s) => s === signatures[0]);
  }, [previewByProgramId, recipientsWithPreview]);

  const carouselRecipients = allShareSameSchedule
    ? recipientsWithPreview.slice(0, 1)
    : recipientsWithPreview;

  return (
    <ModalDialog
      title="Mail sequence preview"
      isOpen={isOpen}
      setClosed={onClose}
      width="large"
      overflow="auto"
      hideFooter
      bodyClassName="py-2"
    >
      {loading && (
        <div className="flex justify-center py-12">
          <Spinner className="h-8 w-8 text-neutral-125" />
        </div>
      )}
      {!loading && hasPreviewData && (
        <div className="space-y-6">
          <div className="px-1">
            <p className="text-body text-neutral-150">
              The following clients will be enrolled in this Mail sequence:{' '}
              <span className="font-medium text-secondary-150">
                {namesLine}
              </span>
            </p>

            <div className="mt-3">
              <ClientCountBreakdown
                totalClients={totalClients}
                selectedTags={selectedTags}
                clientsWithoutTagsCount={clientsWithoutTagsCount}
              />
            </div>

            <p className="mt-8 text-body text-neutral-125">
              {mailRange} {timingClause}
              {sendClause}.
            </p>
          </div>

          {recipientsWithPreview.some(
            (r) => previewByProgramId[r.programInstanceId]?.error,
          ) && (
            <div className="space-y-2 px-1">
              {recipientsWithPreview.map((recipient) => {
                const preview = previewByProgramId[recipient.programInstanceId];
                if (!preview?.error) return null;
                return (
                  <p
                    key={recipient.programInstanceId}
                    className="text-caption text-red-150"
                  >
                    {recipient.title}: {preview.error}
                  </p>
                );
              })}
            </div>
          )}

          {carouselRecipients.map((recipient) => {
            const preview = previewByProgramId[recipient.programInstanceId];
            if (!preview || preview.error) return null;

            return (
              <div key={recipient.programInstanceId}>
                {!allShareSameSchedule ? (
                  <div className="mb-2 px-1 text-caption font-medium text-neutral-125">
                    {recipient.title}
                  </div>
                ) : null}
                <div className="-mx-1 flex snap-x snap-mandatory items-stretch gap-6 overflow-x-auto overscroll-x-contain px-3 pb-5 pt-3">
                  {preview.steps.map((previewStep) => {
                    const draft = steps[previewStep.stepIndex];
                    if (!draft) return null;

                    const { suffix, secondary } = buildCaption(
                      previewStep.scheduledDate,
                      preview.referenceDate,
                      anchorLabel,
                      previewStep.willBeSkippedAtEnroll,
                    );

                    return (
                      <div
                        key={previewStep.stepIndex}
                        className="flex shrink-0 self-stretch"
                      >
                        <ScheduledSendCard
                          interactive={false}
                          send={{
                            stepIndex: previewStep.stepIndex,
                            subject: draft.subject,
                            providerNote: draft.providerNote,
                            status: previewStep.willBeSkippedAtEnroll
                              ? MailSequenceScheduledSendStatus.Skipped
                              : MailSequenceScheduledSendStatus.Pending,
                            skipReason: previewStep.skipReason,
                            activityCount: getStepActivityCount(
                              previewStep.stepIndex,
                              defaultActivities,
                              stepActivitiesState,
                              stepResourceModes,
                            ),
                          }}
                          captionSuffix={suffix}
                          captionSecondary={secondary}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {!loading && !hasPreviewData && (
        <p className="text-small-caption text-neutral-110">
          Add recipients and complete step details, then preview again to see
          when each step will be delivered.
        </p>
      )}
    </ModalDialog>
  );
};

export default SchedulePreviewModal;
